import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PALETTE } from '../pages/site/SiteLayout';
import {
  FileText, LayoutGrid, PackageCheck, Truck, Landmark, ArrowRight, Check,
} from 'lucide-react';

const STAGE_MS = 5200;
const TICK_MS = 60;

const STAGES = [
  { icon: FileText, title: 'PO arrives', line: 'Staff type the customer PO into Sales Order Entry. SKU codes autocomplete.' },
  { icon: LayoutGrid, title: 'Weekly pipeline', line: 'The order lands in the production week alongside every other open order.' },
  { icon: PackageCheck, title: 'Actual production', line: 'Ops record real batches and weights. 100kg ordered becomes 108.2kg actual.' },
  { icon: Truck, title: 'Dispatch', line: 'One click: retailer-format dispatch note, GS1-128 pallet label, COA.' },
  { icon: Landmark, title: 'Invoice, per the deal', line: 'Fixed-price retail SKUs invoice at PO quantity; per-kg trade invoices at actual catch weight. Reality stays in the ops layer.' },
];

const mono = "'SF Mono', 'Fira Code', Menlo, monospace";
const serif = "'Playfair Display', Georgia, serif";

const DocShell = ({ children, badge, badgeColor = PALETTE.magenta, style }) => (
  <div style={{
    background: '#fff', borderRadius: 10, border: `1px solid ${PALETTE.border}`,
    boxShadow: '0 20px 50px -18px rgba(10,37,64,0.28)',
    padding: '20px 22px', position: 'relative', width: '100%', maxWidth: 440,
    ...style,
  }}>
    {badge && (
      <div style={{
        position: 'absolute', top: -11, right: 16, background: badgeColor, color: '#fff',
        fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase',
        padding: '4px 10px', borderRadius: 99, animation: 'dj-pulse 1.6s ease-in-out infinite',
      }}>{badge}</div>
    )}
    {children}
  </div>
);

const DocHeader = ({ label, ref_ }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: `2px solid ${PALETTE.navy}`, paddingBottom: 10, marginBottom: 12 }}>
    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: PALETTE.navy }}>{label}</div>
    <div style={{ fontFamily: mono, fontSize: 12, color: PALETTE.muted }}>{ref_}</div>
  </div>
);

const Row = ({ k, v, strong, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '5px 0' }}>
    <span style={{ color: PALETTE.muted }}>{k}</span>
    <span style={{ fontFamily: mono, fontWeight: strong ? 700 : 500, color: color || PALETTE.ink }}>{v}</span>
  </div>
);

/* ---------- Stage visuals ---------- */

const StagePO = () => (
  <DocShell badge="NEW PO">
    <DocHeader label="Purchase Order" ref_="PO-7841" />
    <Row k="Customer" v="Retail Multiple, Dublin" />
    <Row k="Their SKU" v="TC-CHED-200" />
    <div className="dj-type" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '5px 0' }}>
      <span style={{ color: PALETTE.muted }}>Resolves to</span>
      <span style={{ fontFamily: mono, fontWeight: 700, color: PALETTE.magenta }}>Vintage Cheddar 200g ✓</span>
    </div>
    <Row k="Ordered qty" v="100.0 kg" strong />
    <Row k="Delivery" v="Thu, wk 24" />
  </DocShell>
);

const PIPE = [
  { d: 'Tue', rows: [['SO-118 · Deli counter', false], ['SO-121 · Wholesaler', false]] },
  { d: 'Wed', rows: [['SO-119 · Foodservice', false]] },
  { d: 'Thu', rows: [['SO-120 · Farm shop', false], ['PO-7841 · Retail Multiple', true]] },
];

const StagePipeline = () => (
  <DocShell badge="WEEK 24" badgeColor={PALETTE.navy}>
    <DocHeader label="Weekly pipeline" ref_="All open orders" />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
      {PIPE.map((col) => (
        <div key={col.d}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', color: PALETTE.muted, textTransform: 'uppercase', marginBottom: 6 }}>{col.d}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {col.rows.map(([label, hot]) => (
              <div key={label} className={hot ? 'dj-land' : undefined} style={{
                fontSize: 10.5, lineHeight: 1.3, padding: '7px 8px', borderRadius: 6,
                background: hot ? '#FDE7F0' : PALETTE.cream,
                border: `1px solid ${hot ? PALETTE.magenta : PALETTE.border}`,
                fontWeight: hot ? 700 : 500, color: hot ? PALETTE.magentaDark : PALETTE.slate,
                fontFamily: mono,
              }}>{label}</div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </DocShell>
);

const StageProduction = () => (
  <DocShell badge="ON THE FLOOR" badgeColor={PALETTE.navy}>
    <DocHeader label="Actual production" ref_="PO-7841" />
    <Row k="Batch KS-0412-A" v="54.6 kg" />
    <div className="dj-type">
      <Row k="Batch KS-0412-B" v="53.6 kg" />
    </div>
    <div style={{ borderTop: `1px dashed ${PALETTE.border}`, marginTop: 6, paddingTop: 6 }}>
      <Row k="Actual total" v="108.2 kg" strong />
      <Row k="PO quantity" v="100.0 kg" />
    </div>
    <div className="dj-rise" style={{
      marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 8,
      background: '#FBF4E4', border: `1px solid ${PALETTE.gold}`, borderRadius: 99,
      padding: '6px 14px', fontFamily: mono, fontSize: 12.5, fontWeight: 700, color: '#8A6D2B',
    }}>+8.2 kg vs PO, captured, not hidden</div>
  </DocShell>
);

const StageDispatch = () => (
  <DocShell badge="1 CLICK" >
    <DocHeader label="Dispatch note" ref_="Retailer template ✓" />
    <Row k="Format" v="Customer-specific" />
    <Row k="Dispatched" v="108.2 kg · 2 batches" strong />
    <div style={{ margin: '12px 0 8px', height: 34, borderRadius: 4, background: `repeating-linear-gradient(90deg, ${PALETTE.ink} 0 3px, transparent 3px 6px, ${PALETTE.ink} 6px 7px, transparent 7px 12px)` }} />
    <div style={{ fontFamily: mono, fontSize: 10.5, letterSpacing: '0.2em', color: PALETTE.muted, textAlign: 'center' }}>(01)0539512345678(10)KS-0412</div>
    <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
      {['GS1-128 pallet label', 'COA attached', 'Depot-ready'].map((c) => (
        <span key={c} className="dj-rise" style={{
          display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700,
          color: PALETTE.navy, background: PALETTE.cream, border: `1px solid ${PALETTE.border}`,
          borderRadius: 99, padding: '4px 10px',
        }}><Check size={11} strokeWidth={3} color={PALETTE.magenta} />{c}</span>
      ))}
    </div>
  </DocShell>
);

const StageSplit = () => (
  <div style={{ width: '100%', maxWidth: 460, display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'stretch' }}>
    <div className="dj-split-left">
      <DocShell badge="TO ACCOUNTS" style={{ maxWidth: 'none', borderTop: `3px solid ${PALETTE.magenta}` }}>
        <DocHeader label="Invoice" ref_="INV-2214 → Xero / AccountsIQ" />
        <Row k="Trading mode" v="Fixed-price PO" />
        <Row k="Vintage Cheddar 200g" v="100.0 kg" strong />
        <Row k="Matches PO-7841" v="✓ exact" color="#1B7F4D" strong />
        <div style={{ fontSize: 12, color: PALETTE.muted, marginTop: 8 }}>Pushed over API, matches the retailer's PO, so AP pays without a query. Per-kg customers invoice at actual catch weight (108.2 kg) instead.</div>
      </DocShell>
    </div>
    <div className="dj-split-drop" style={{
      alignSelf: 'flex-end', width: '86%',
      background: PALETTE.navy, color: '#fff', borderRadius: 10, padding: '14px 18px',
      borderLeft: `4px solid ${PALETTE.gold}`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
    }}>
      <div>
        <div style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: PALETTE.gold, marginBottom: 3 }}>Recorded in the operations layer</div>
        <div style={{ fontFamily: mono, fontSize: 13, fontWeight: 700 }}>+8.2 kg · KS-0412-A/B · traced & reported</div>
      </div>
      <PackageCheck size={22} color={PALETTE.gold} />
    </div>
  </div>
);

const STAGE_VIEWS = [StagePO, StagePipeline, StageProduction, StageDispatch, StageSplit];

/* ---------- Main component ---------- */

export default function DocumentJourney({ showCta = true }) {
  const [stage, setStage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [started, setStarted] = useState(false);
  const pausedRef = useRef(false);
  const manualUntilRef = useRef(0);
  const rootRef = useRef(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || started) return undefined;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setStarted(true); obs.disconnect(); }
    }, { threshold: 0.35 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return undefined;
    const id = setInterval(() => {
      if (pausedRef.current || Date.now() < manualUntilRef.current) return;
      setProgress((p) => {
        const next = p + (TICK_MS / STAGE_MS) * 100;
        if (next >= 100) {
          setStage((s) => (s + 1) % STAGES.length);
          return 0;
        }
        return next;
      });
    }, TICK_MS);
    return () => clearInterval(id);
  }, [started]);

  const jump = useCallback((i) => {
    setStage(i);
    setProgress(0);
    manualUntilRef.current = Date.now() + 9000;
  }, []);

  const ViewComp = STAGE_VIEWS[stage];

  return (
    <section
      ref={rootRef}
      data-testid="document-journey"
      style={{
        background: PALETTE.cream,
        padding: '90px 32px',
        borderTop: `1px solid ${PALETTE.border}`,
        borderBottom: `1px solid ${PALETTE.border}`,
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', maxWidth: 780, margin: '0 auto 52px' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>
            How it works, from PO to invoice
          </div>
          <h2 style={{
            fontFamily: serif, fontSize: 'clamp(30px, 4vw, 48px)', fontWeight: 700,
            color: PALETTE.navy, margin: '0 0 18px', letterSpacing: '-0.02em', lineHeight: 1.05,
          }}>
            Watch one PO travel{' '}
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>
              the whole way through.
            </em>
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.6, color: PALETTE.slate, margin: 0 }}>
            100kg ordered. 108.2kg made. The invoice follows the deal, the 100kg PO for a fixed-price retail SKU, the actual 108.2kg catch weight for per-kg trade, and nothing gets hidden. That mode-aware split at step five is why food producers pick Keystone over a generic ERP.
          </p>
        </div>

        <div className="dj-main" style={{
          display: 'grid', gridTemplateColumns: '380px 1fr', gap: 40, alignItems: 'stretch',
        }}>
          {/* Stepper */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {STAGES.map((s, i) => {
              const Icon = s.icon;
              const active = i === stage;
              const done = i < stage;
              return (
                <button
                  key={s.title}
                  type="button"
                  data-testid={`journey-step-${i + 1}`}
                  onClick={() => jump(i)}
                  style={{
                    textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                    background: active ? '#fff' : 'transparent',
                    border: `1px solid ${active ? PALETTE.border : 'transparent'}`,
                    borderRadius: 10, padding: '14px 16px',
                    boxShadow: active ? '0 10px 28px -14px rgba(10,37,64,0.25)' : 'none',
                    transition: 'background-color .3s, box-shadow .3s',
                    display: 'grid', gridTemplateColumns: '36px 1fr', gap: 14, alignItems: 'start',
                    position: 'relative', overflow: 'hidden',
                  }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, marginTop: 2,
                    background: active ? PALETTE.magenta : done ? PALETTE.navy : '#EDEAE2',
                    color: active || done ? '#fff' : PALETTE.muted,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background-color .3s, color .3s',
                  }}>
                    {done ? <Check size={17} strokeWidth={3} /> : <Icon size={17} strokeWidth={2.2} />}
                  </div>
                  <div>
                    <div style={{
                      fontSize: 14.5, fontWeight: 700, letterSpacing: '-0.01em',
                      color: active ? PALETTE.navy : PALETTE.slate, marginBottom: 3,
                      display: 'flex', alignItems: 'baseline', gap: 8,
                    }}>
                      <span style={{ fontFamily: mono, fontSize: 11, color: active ? PALETTE.magenta : PALETTE.muted }}>0{i + 1}</span>
                      {s.title}
                    </div>
                    <div style={{
                      fontSize: 12.5, lineHeight: 1.5, color: PALETTE.muted,
                      maxHeight: active ? 60 : 0, opacity: active ? 1 : 0, overflow: 'hidden',
                      transition: 'max-height .35s ease, opacity .3s ease',
                    }}>{s.line}</div>
                  </div>
                  {active && (
                    <div style={{ position: 'absolute', left: 0, bottom: 0, height: 3, width: `${progress}%`, background: PALETTE.magenta, transition: 'width .06s linear' }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Document viewport */}
          <div
            data-testid="journey-viewport"
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
            style={{
              background: `radial-gradient(ellipse at 30% 20%, ${PALETTE.navyMid} 0%, ${PALETTE.navy} 55%, ${PALETTE.navyDeep} 100%)`,
              borderRadius: 14, minHeight: 380,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '36px 28px', position: 'relative', overflow: 'hidden',
            }}
          >
            <div aria-hidden="true" style={{
              position: 'absolute', inset: 0, opacity: 0.5,
              backgroundImage: `radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)`,
              backgroundSize: '22px 22px',
            }} />
            <div key={stage} className="dj-enter" style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
              <ViewComp />
            </div>
            <div style={{
              position: 'absolute', bottom: 14, left: 0, right: 0, textAlign: 'center',
              fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.45)', fontWeight: 700,
            }}>
              Hover to pause · click a step to explore
            </div>
          </div>
        </div>

        {/* Kill-shot callout */}
        <div className="dj-callout" style={{
          marginTop: 40, padding: '26px 30px', background: PALETTE.navy, color: '#fff',
          borderRadius: 8, borderLeft: `4px solid ${PALETTE.gold}`,
          display: 'grid', gridTemplateColumns: '1fr auto', gap: 20, alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.20em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 10 }}>
              Why this matters
            </div>
            <div style={{ fontFamily: serif, fontSize: 'clamp(22px, 2.4vw, 30px)', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em', lineHeight: 1.2, marginBottom: 10 }}>
              You dispatched 108kg. What should the invoice say?
            </div>
            <div style={{ fontSize: 14.5, lineHeight: 1.6, color: '#CBD5E1', maxWidth: 640 }}>
              Depends on the deal. Fixed-price retail SKU: the <strong style={{ color: '#fff', fontWeight: 700 }}>100kg PO quantity</strong>, within tolerance, so the retailer's AP matches and pays. Per-kg trade: the <strong style={{ color: '#fff', fontWeight: 700 }}>actual 108kg catch weight</strong>. Keystone knows each customer's mode, and the operations layer keeps the real weights and batches either way.
            </div>
          </div>
          {showCta && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Link to="/site/how-it-works" data-testid="journey-cta" style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '14px 22px', background: PALETTE.magenta, color: '#fff',
                borderRadius: 6, fontSize: 13, fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', textDecoration: 'none', whiteSpace: 'nowrap',
              }}>
                See every step <ArrowRight size={16} />
              </Link>
              <Link to="/site/pricing" data-testid="journey-pricing-cta" style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '13px 22px', background: 'transparent', color: '#fff',
                border: '1px solid rgba(255,255,255,0.35)',
                borderRadius: 6, fontSize: 13, fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', textDecoration: 'none', whiteSpace: 'nowrap',
              }}>
                See pricing
              </Link>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes dj-in { from { opacity: 0; transform: translateY(18px) scale(0.97); } to { opacity: 1; transform: none; } }
        @keyframes dj-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
        @keyframes dj-land { 0% { opacity: 0; transform: translateY(-14px); } 60% { opacity: 1; transform: translateY(3px); } 100% { transform: none; } }
        @keyframes dj-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
        @keyframes dj-type { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dj-left { from { opacity: 0; transform: translateX(-30px); } to { opacity: 1; transform: none; } }
        @keyframes dj-drop { 0% { opacity: 0; transform: translate(40px, -30px) scale(0.9); } 55% { opacity: 1; transform: translate(0, 6px) scale(1.01); } 100% { transform: none; } }
        .dj-enter { animation: dj-in .5s cubic-bezier(.2,.8,.25,1) both; }
        .dj-land { animation: dj-land .7s .5s cubic-bezier(.2,.8,.25,1) both; }
        .dj-rise { animation: dj-rise .5s .9s ease both; }
        .dj-type { animation: dj-type .4s .7s ease both; }
        .dj-split-left { animation: dj-left .55s .15s cubic-bezier(.2,.8,.25,1) both; }
        .dj-split-drop { animation: dj-drop .8s .8s cubic-bezier(.2,.8,.25,1) both; }
        @media (prefers-reduced-motion: reduce) {
          .dj-enter, .dj-land, .dj-rise, .dj-type, .dj-split-left, .dj-split-drop { animation: none !important; }
        }
        @media (max-width: 980px) {
          .dj-main { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 520px) {
          .dj-callout { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
